import { CalendarDays, CalendarX2 } from "lucide-react";
import Loader from "../../../components/Loader/Loader";
import StatusChip from "../../../components/StatusChip/StatusChip";
import AttendanceCalendar from "./components/AttendanceCalendar";
import useStudentAttendance from "./hooks/useStudentAttendance";
import { useStudentSession } from "../context/StudentSessionContext";
import "./Attendance.css";

const LEGEND = [
  { label: "Present", tone: "success" },
  { label: "Absent", tone: "danger" },
  { label: "Leave", tone: "info" },
  { label: "Holiday", tone: "warning" },
];

// Field names for the summary card are read defensively (snake_case and
// camelCase both) since the attendance-summary response shape isn't final.
const StudentAttendance = () => {
  const { student } = useStudentSession();
  const programId = student?.student_program?.program_id;
  const sessionId = student?.student_program?.session_id;
  const { summary, days, holidays, isLoading, isMonthLoading, month, setMonth } = useStudentAttendance(
    student?.id,
    programId,
    sessionId
  );

  if (isLoading) {
    return <Loader />;
  }

  // Confirmed against the real backend (2026-10-22): total_days,
  // present_days, absent_days, attendance_percentage.
  const totalDays = summary?.total_days ?? 0;
  const daysAttended = summary?.present_days ?? 0;
  const daysAbsent = summary?.absent_days ?? 0;
  const rawPercentage = summary?.attendance_percentage;
  const percentage = rawPercentage != null
    ? Math.round(Number(rawPercentage))
    : totalDays
    ? Math.round((daysAttended / totalDays) * 100)
    : 0;

  return (
    <div className="student-attendance">
      <div className="attendance-left">
        <div className="attendance-summary-card">
          <h3 className="attendance-summary-title">Attendance Summary</h3>

          <div className="attendance-summary-row">
            <span>Total Days</span>
            <strong>{totalDays}</strong>
          </div>

          <div className="attendance-progress">
            <div className="attendance-progress-track">
              <div
                className="attendance-progress-fill"
                style={{ width: `${Math.min(Math.max(percentage, 0), 100)}%` }}
              />
            </div>
            <span className="attendance-progress-label">{percentage}%</span>
          </div>

          <div className="attendance-summary-footer">
            <div>
              <span>Days Attended</span>
              <strong>{daysAttended}</strong>
            </div>
            <div>
              <span>Days Absent</span>
              <strong>{daysAbsent}</strong>
            </div>
          </div>
        </div>

        <div className="attendance-legend">
          {LEGEND.map((item) => (
            <StatusChip key={item.label} tone={item.tone}>
              {item.label}
            </StatusChip>
          ))}
        </div>

        <div className="sp-card attendance-holidays-card">
          <h4 className="sp-section-title">Holidays</h4>

          {holidays.length === 0 ? (
            <div className="sp-empty-state">
              <CalendarX2 size={40} strokeWidth={1.5} />
              <p>There are no holidays!</p>
            </div>
          ) : (
            <ul className="attendance-holidays-list">
              {holidays.map((holiday, index) => (
                <li key={`${holiday.date}-${index}`} className="attendance-holiday-row">
                  <span className="sp-icon-tile attendance-holiday-icon" data-tone="warning">
                    <CalendarDays size={16} />
                  </span>
                  <span className="attendance-holiday-text">
                    <span className="attendance-holiday-name">{holiday.name}</span>
                    <span className="attendance-holiday-date">{holiday.date}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="attendance-right">
        <AttendanceCalendar month={month} days={days} onMonthChange={setMonth} isLoading={isMonthLoading} />
      </div>
    </div>
  );
};

export default StudentAttendance;
